import React, { useState } from "react";
import axios from "axios";
import Marquee from "react-fast-marquee";
import { Toaster, toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Home,
  HandHeart,
  Users,
  Shield,
  Building2,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Globe,
  Instagram,
  Facebook,
  Linkedin,
  ArrowRight,
} from "lucide-react";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_VIDEO =
  "https://cdn.coverr.co/videos/coverr-modern-mansion-exterior-2882/1080p.mp4";
const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=2000&q=80";

const SERVICES = [
  {
    title: "Buyer Representation",
    blurb:
      "Discreet, white-glove guidance for first homes, second residences, and investment acquisitions.",
    points: ["Off-market access", "Negotiation strategy", "Due diligence"],
    icon: Home,
  },
  {
    title: "Seller Listing & Marketing",
    blurb:
      "Cinematic listing campaigns, editorial photography and concierge staging to maximize value.",
    points: ["Pricing strategy", "Editorial media", "Qualified-buyer network"],
    icon: Building2,
  },
  {
    title: "Investment Advisory",
    blurb:
      "Data-led analysis on cash flow, appreciation and tax-advantaged structures across markets.",
    points: ["Market modeling", "ROI projections", "1031 strategy"],
    icon: ChevronRight,
  },
  {
    title: "Portfolio Management",
    blurb:
      "Hands-on stewardship for multi-property owners — leasing, asset performance, and growth planning.",
    points: ["Asset oversight", "Tenant relations", "Long-term planning"],
    icon: Shield,
  },
];

const COMMUNITY = [
  {
    title: "Youth Coaching",
    desc:
      "Mentoring young athletes and students — building confidence, discipline and possibility.",
    img:
      "https://images.unsplash.com/photo-1526232761682-d26e03ac148e?auto=format&fit=crop&w=1200&q=80",
    icon: Users,
  },
  {
    title: "Volunteering",
    desc:
      "Boots-on-the-ground hours across food banks, shelters and neighborhood revitalization.",
    img:
      "https://images.unsplash.com/photo-1593113598332-cd288d649433?auto=format&fit=crop&w=1200&q=80",
    icon: HandHeart,
  },
  {
    title: "Veterans Support",
    desc:
      "Homeownership programs and transition resources for those who served.",
    img:
      "https://images.unsplash.com/photo-1591826329474-9cf68d29df87?auto=format&fit=crop&w=1200&q=80",
    icon: Shield,
  },
  {
    title: "Low-Income Housing",
    desc:
      "Reinvesting into affordable housing initiatives — dignified, beautiful homes for every family.",
    img:
      "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1200&q=80",
    icon: Building2,
  },
];

const GALLERY = [
  {
    src:
      "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=1400&q=80",
    span: "bento-big",
    alt: "Modern glass and stone luxury home at twilight",
  },
  {
    src:
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80",
    span: "bento-tall",
    alt: "Architectural luxury residence",
  },
  {
    src:
      "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1200&q=80",
    span: "",
    alt: "Sunlit modern interior",
  },
  {
    src:
      "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1400&q=80",
    span: "bento-wide",
    alt: "Designer kitchen with island",
  },
  {
    src:
      "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1200&q=80",
    span: "",
    alt: "Pool and patio of a contemporary estate",
  },
  {
    src:
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200&q=80",
    span: "",
    alt: "Open-plan luxury living room",
  },
];

const NAV_LINKS = [
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#community", label: "Community" },
  { href: "#gallery", label: "Gallery" },
  { href: "#contact", label: "Contact" },
];

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#FAF9F6]/80 border-b border-black/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="logo-link"
          className="font-heading text-xl md:text-2xl tracking-wider"
        >
          GRIDDOFF<span className="text-[#C29B62]">.</span>HOMES
        </a>
        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm tracking-wide text-[#2D312E]/80 hover:text-[#C29B62] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <a
          href="#contact"
          data-testid="header-cta-button"
          className="hidden md:inline-flex btn-outline text-xs"
        >
          Free Consultation
        </a>
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          <div className="w-6 h-px bg-[#2D312E] mb-1.5" />
          <div className="w-6 h-px bg-[#2D312E] mb-1.5" />
          <div className="w-4 h-px bg-[#2D312E]" />
        </button>
      </div>
      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-black/5 bg-[#FAF9F6]"
        >
          <div className="px-6 py-4 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="text-sm tracking-wide"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-header-cta-button"
              className="btn-outline text-xs self-start"
            >
              Free Consultation
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center grain overflow-hidden"
    >
      <video
        data-testid="hero-video"
        autoPlay
        muted
        loop
        playsInline
        poster={HERO_FALLBACK}
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 w-full">
        <p
          data-testid="hero-kicker"
          className="kicker text-[#C29B62] reveal"
          style={{ animationDelay: "0.05s" }}
        >
          Griddoff Homes LLC · Luxury Real Estate Investment
        </p>
        <h1
          data-testid="hero-title"
          className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light leading-[0.95] tracking-tight text-white mt-6 max-w-4xl reveal delay-100"
        >
          Luxury living. <br />
          <em className="text-[#C29B62] not-italic font-light">Community first.</em>
        </h1>
        <p
          data-testid="hero-subtitle"
          className="mt-8 max-w-xl text-base md:text-lg text-white/85 leading-relaxed reveal delay-200"
        >
          We curate exceptional properties and reinvest{" "}
          <span className="text-[#C29B62] font-medium">5% of every transaction</span>{" "}
          into the communities we serve — open hearts, open doors, for everyone.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal delay-300">
          <a
            href="#contact"
            data-testid="hero-cta-primary"
            className="btn-primary inline-flex items-center justify-center gap-2"
          >
            Request Free Consultation <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="#services"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center justify-center gap-2 text-white border border-white/30 hover:border-white px-7 py-4 font-semibold tracking-wide transition-all duration-300"
          >
            Explore Services
          </a>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/50 text-xs tracking-[0.3em] uppercase">
        Scroll
      </div>
    </section>
  );
}

function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-36 bg-[#FAF9F6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20 items-center">
        <div className="md:col-span-6 relative">
          <img
            src="https://images.unsplash.com/photo-1564078516393-cf04bd966897?auto=format&fit=crop&w=1400&q=80"
            alt="Luxury interior living room"
            className="w-full h-[460px] md:h-[620px] object-cover"
            data-testid="about-image"
          />
          <div className="hidden md:flex absolute -bottom-10 -right-10 bg-white p-8 max-w-xs border border-[#E5E2DC] shadow-lg">
            <div>
              <div className="font-heading text-5xl text-[#C29B62] leading-none">5%</div>
              <div className="kicker mt-3">Of every proceed</div>
              <p className="mt-3 text-sm text-[#2D312E]/75 leading-relaxed">
                Reinvested into youth, veterans, volunteering and affordable housing.
              </p>
            </div>
          </div>
        </div>
        <div className="md:col-span-6">
          <p className="kicker" data-testid="about-kicker">
            Our Mission
          </p>
          <h2
            data-testid="about-title"
            className="font-heading text-4xl sm:text-5xl md:text-6xl font-light leading-tight mt-4"
          >
            Beautiful homes — built on belonging.
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-[#2D312E]/80">
            Griddoff Homes LLC is a boutique luxury real estate investment firm.
            We help discerning clients buy, sell, and grow portfolios of remarkable
            properties — and we use a meaningful share of every transaction to
            uplift the neighborhoods we love.
          </p>
          <p className="mt-4 text-lg leading-relaxed text-[#2D312E]/80">
            We welcome <strong className="font-medium text-[#2D312E]">everyone</strong>{" "}
            — every background, every belief, every love story. Black, brown, Indigenous,
            Asian, LGBTQ+, immigrant, faith-led or secular: this is a doorway
            built for you.
          </p>
          <ul className="mt-8 grid sm:grid-cols-2 gap-4">
            {[
              "White-glove client care",
              "Discreet & data-led",
              "Community-first ethic",
              "Inclusive by design",
            ].map((v) => (
              <li
                key={v}
                className="flex items-start gap-3 text-[#2D312E]/85"
                data-testid={`about-value-${v.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="mt-2 w-2 h-2 bg-[#C29B62] rounded-full shrink-0" />
                <span>{v}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-36 bg-[#F3F2EE]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div className="max-w-2xl">
            <p className="kicker" data-testid="services-kicker">
              Services & Pricing
            </p>
            <h2
              data-testid="services-title"
              className="font-heading text-4xl sm:text-5xl md:text-6xl font-light leading-tight mt-4"
            >
              Tailored counsel at every step.
            </h2>
          </div>
          <p className="text-[#2D312E]/70 md:text-right md:max-w-sm leading-relaxed">
            Every engagement begins with a complimentary, no-pressure consultation —
            so we can listen first.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                data-testid={`service-card-${i}`}
                className="service-card group"
              >
                <Icon className="w-7 h-7 text-[#C29B62] mb-6" strokeWidth={1.5} />
                <h3 className="font-heading text-2xl font-medium leading-tight">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm text-[#2D312E]/70 leading-relaxed">
                  {s.blurb}
                </p>
                <ul className="mt-5 space-y-2 text-sm text-[#2D312E]/80 flex-grow">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-[#C29B62] rounded-full" />
                      {p}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-[#E5E2DC]">
                  <div className="text-xs uppercase tracking-[0.18em] text-[#2D312E]/55">
                    Investment
                  </div>
                  <div className="font-heading text-2xl mt-1">
                    Custom · By engagement
                  </div>
                  <a
                    href="#contact"
                    data-testid={`service-cta-${i}`}
                    className="mt-5 inline-flex items-center gap-2 text-[#C29B62] hover:text-[#A88554] font-semibold text-sm tracking-wide transition-colors"
                  >
                    Request Free Consultation
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Community() {
  return (
    <section
      id="community"
      data-testid="community-section"
      className="bg-[#1A1D1A] text-[#F3F2EE] py-24 md:py-36 relative"
    >
      <Marquee
        gradient={false}
        speed={40}
        className="border-y border-white/10 py-6 mb-20"
      >
        {[
          "Youth Coaching",
          "Volunteering",
          "Veterans Support",
          "Low-Income Housing",
          "5% Pledge",
        ].map((w, i) => (
          <span key={i} className="marquee-item">
            {w} <span className="marquee-dot" />
          </span>
        ))}
      </Marquee>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 items-end mb-16">
          <div className="md:col-span-7">
            <p className="kicker">The 5% Pledge</p>
            <h2
              data-testid="community-title"
              className="font-heading text-4xl sm:text-5xl md:text-6xl font-light mt-4 leading-tight"
            >
              Where luxury <em className="text-[#C29B62] not-italic">gives back</em>.
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-white/70 leading-relaxed">
              We commit 5% of every transaction to four causes close to our heart —
              not as charity, but as kinship. This is what real wealth looks like.
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {COMMUNITY.map((c, i) => {
            const Icon = c.icon;
            return (
              <article
                key={c.title}
                data-testid={`community-card-${i}`}
                className="group relative overflow-hidden bg-[#22251F] border border-white/5"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={c.img}
                    alt={c.title}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1D1A] via-[#1A1D1A]/30 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-6">
                  <Icon className="w-6 h-6 text-[#C29B62] mb-3" strokeWidth={1.5} />
                  <h3 className="font-heading text-2xl mb-2">{c.title}</h3>
                  <p className="text-sm text-white/70 leading-relaxed">{c.desc}</p>
                </div>
              </article>
            );
          })}
        </div>
        <p className="mt-12 text-center text-sm text-white/55 max-w-2xl mx-auto leading-relaxed">
          We stand with all people — LGBTQ+, Black, brown, Indigenous, immigrant,
          faith-led and secular communities alike. Every home we open is a home
          that opens its arms back.
        </p>
      </div>
    </section>
  );
}

function Gallery() {
  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-24 md:py-36 bg-[#FAF9F6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="kicker">Featured Properties</p>
            <h2
              data-testid="gallery-title"
              className="font-heading text-4xl sm:text-5xl md:text-6xl font-light mt-4 leading-tight"
            >
              A portfolio of remarkable homes.
            </h2>
          </div>
          <a
            href="#contact"
            data-testid="gallery-cta"
            className="btn-outline text-xs self-start md:self-end inline-flex items-center gap-2"
          >
            See more privately <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
        <div className="bento">
          {GALLERY.map((g, i) => (
            <div
              key={i}
              data-testid={`gallery-item-${i}`}
              className={`relative overflow-hidden group ${g.span}`}
            >
              <img
                src={g.src}
                alt={g.alt}
                className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — we'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", service_interest: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email us directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="contact-form"
      className="grid sm:grid-cols-2 gap-6"
    >
      <div className="sm:col-span-1">
        <label className="lux-label" htmlFor="contact-name">
          Full Name *
        </label>
        <input
          id="contact-name"
          data-testid="contact-name-input"
          className="lux-input"
          value={form.name}
          onChange={update("name")}
          placeholder="Your name"
          required
        />
      </div>
      <div className="sm:col-span-1">
        <label className="lux-label" htmlFor="contact-email">
          Email *
        </label>
        <input
          id="contact-email"
          type="email"
          data-testid="contact-email-input"
          className="lux-input"
          value={form.email}
          onChange={update("email")}
          placeholder="you@email.com"
          required
        />
      </div>
      <div className="sm:col-span-1">
        <label className="lux-label" htmlFor="contact-phone">
          Phone
        </label>
        <input
          id="contact-phone"
          data-testid="contact-phone-input"
          className="lux-input"
          value={form.phone}
          onChange={update("phone")}
          placeholder="Optional"
        />
      </div>
      <div className="sm:col-span-1">
        <label className="lux-label" htmlFor="contact-service">
          Service of Interest
        </label>
        <select
          id="contact-service"
          data-testid="contact-service-select"
          className="lux-input bg-transparent"
          value={form.service_interest}
          onChange={update("service_interest")}
        >
          <option value="">Select a service</option>
          {SERVICES.map((s) => (
            <option key={s.title} value={s.title}>
              {s.title}
            </option>
          ))}
          <option value="Other">Other / General inquiry</option>
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className="lux-label" htmlFor="contact-message">
          How can we help? *
        </label>
        <textarea
          id="contact-message"
          data-testid="contact-message-input"
          className="lux-input"
          value={form.message}
          onChange={update("message")}
          placeholder="Tell us a little about what you're looking for…"
          rows={4}
          required
        />
      </div>
      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-2">
        <p className="text-xs text-[#2D312E]/55 max-w-md leading-relaxed">
          By submitting this form you agree to our Privacy Policy. We never sell
          your data and only contact you about your inquiry.
        </p>
        <button
          type="submit"
          data-testid="contact-submit-button"
          className="btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-60"
          disabled={submitting}
        >
          {submitting ? "Sending…" : "Request Free Consultation"}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}

function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-36 bg-[#FAF9F6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20">
        <div className="md:col-span-5">
          <p className="kicker">Get in Touch</p>
          <h2
            data-testid="contact-title"
            className="font-heading text-4xl sm:text-5xl md:text-6xl font-light mt-4 leading-tight"
          >
            Let&apos;s find your <em className="text-[#C29B62] not-italic">next chapter</em>.
          </h2>
          <p className="mt-6 text-[#2D312E]/75 leading-relaxed">
            Tell us what you&apos;re dreaming of — a first home, a generational estate,
            an investment portfolio. We respond personally within one business day.
          </p>
          <div className="mt-10 space-y-5 text-sm">
            <div className="flex items-start gap-4" data-testid="contact-info-email">
              <Mail className="w-5 h-5 text-[#C29B62] mt-0.5" strokeWidth={1.5} />
              <div>
                <div className="kicker mb-1">Email</div>
                <div>stephen.adesuyi@griddoffhomes.com</div>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-info-phone">
              <Phone className="w-5 h-5 text-[#C29B62] mt-0.5" strokeWidth={1.5} />
              <div>
                <div className="kicker mb-1">Phone</div>
                <div>888-874-6850</div>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-info-address">
              <MapPin className="w-5 h-5 text-[#C29B62] mt-0.5" strokeWidth={1.5} />
              <div>
                <div className="kicker mb-1">Office</div>
                <div>By appointment · United States</div>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-info-web">
              <Globe className="w-5 h-5 text-[#C29B62] mt-0.5" strokeWidth={1.5} />
              <div>
                <div className="kicker mb-1">Web</div>
                <a
                  href="https://griddoffhomesllc.com"
                  className="hover:text-[#C29B62] transition-colors"
                >
                  griddoffhomesllc.com
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="md:col-span-7 bg-white border border-[#E5E2DC] p-8 md:p-12">
          <ContactForm />
        </div>
      </div>
    </section>
  );
}

const LEGAL_CONTENT = {
  privacy: {
    title: "Privacy Policy",
    body: (
      <>
        <p>
          Griddoff Homes LLC (&quot;we&quot;, &quot;us&quot;) respects your privacy. This policy
          describes how we collect, use, and protect information when you visit
          our website or submit an inquiry.
        </p>
        <h4 className="font-heading text-xl mt-6">Information we collect</h4>
        <p>
          Contact details you voluntarily share (name, email, phone, message),
          and standard analytics about how you use the site.
        </p>
        <h4 className="font-heading text-xl mt-6">How we use it</h4>
        <p>
          Solely to respond to your inquiry, provide our services, and improve
          our website. We never sell your personal data.
        </p>
        <h4 className="font-heading text-xl mt-6">Your rights</h4>
        <p>
          You may request access, correction, or deletion of your information at
          any time by emailing stephen.adesuyi@griddoffhomes.com.
        </p>
      </>
    ),
  },
  disclosure: {
    title: "Disclosure",
    body: (
      <>
        <p>
          Griddoff Homes LLC is a real estate investment company. All
          information on this website is for general informational purposes and
          should not be construed as legal, financial, tax, or investment advice.
        </p>
        <p className="mt-4">
          Real estate investments carry risk. Past performance is not indicative
          of future results. Property availability, pricing, and program details
          are subject to change without notice.
        </p>
        <p className="mt-4">
          We are an Equal Housing Opportunity firm. We do not discriminate on
          the basis of race, color, religion, sex, gender identity, sexual
          orientation, national origin, disability, familial status, or any
          other protected characteristic.
        </p>
      </>
    ),
  },
  terms: {
    title: "Terms & Conditions",
    body: (
      <>
        <p>
          By accessing this website you agree to be bound by these terms. The
          site and all content are the property of Griddoff Homes LLC and
          provided &quot;as is&quot; without warranty.
        </p>
        <h4 className="font-heading text-xl mt-6">Use of the site</h4>
        <p>
          You agree not to misuse the site, attempt to gain unauthorized access,
          or use any content for commercial purposes without written permission.
        </p>
        <h4 className="font-heading text-xl mt-6">Limitation of liability</h4>
        <p>
          To the maximum extent permitted by law, Griddoff Homes LLC is not
          liable for any indirect or consequential damages arising from use of
          this website.
        </p>
        <h4 className="font-heading text-xl mt-6">Governing law</h4>
        <p>
          These terms are governed by the laws of the United States and the
          state in which Griddoff Homes LLC is incorporated.
        </p>
      </>
    ),
  },
  contact: {
    title: "Contact Us",
    body: (
      <>
        <p>We&apos;d love to hear from you. Reach us via any of the following:</p>
        <ul className="mt-4 space-y-2">
          <li>Email: stephen.adesuyi@griddoffhomes.com</li>
          <li>Phone: 888-874-6850</li>
          <li>Office: By appointment · United States</li>
        </ul>
        <p className="mt-4">
          Or use the form on this page to send a private message — we typically
          respond within one business day.
        </p>
      </>
    ),
  },
};

function LegalDialog({ trigger, slug }) {
  const content = LEGAL_CONTENT[slug];
  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid={`legal-dialog-${slug}`}
        className="max-w-2xl bg-[#FAF9F6] border-[#E5E2DC] max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl font-light text-[#2D312E]">
            {content.title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {content.title} for Griddoff Homes LLC
          </DialogDescription>
        </DialogHeader>
        <div className="text-[#2D312E]/75 leading-relaxed pt-2 text-base space-y-3">
          {content.body}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#1A1D1A] text-[#F3F2EE] pt-20 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <div className="font-heading text-2xl tracking-wider mb-4">
              GRIDDOFF<span className="text-[#C29B62]">.</span>HOMES
            </div>
            <p className="text-white/65 leading-relaxed max-w-md">
              A boutique luxury real estate investment firm with deep roots in
              community. 5% of every transaction is reinvested into the lives
              we serve.
            </p>
            <div className="flex gap-4 mt-6">
              {[
                { icon: Instagram, label: "instagram" },
                { icon: Facebook, label: "facebook" },
                { icon: Linkedin, label: "linkedin" },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.label}
                    href="#"
                    data-testid={`social-${s.label}`}
                    aria-label={s.label}
                    className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-[#C29B62] hover:text-[#C29B62] transition-colors"
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                );
              })}
            </div>
          </div>
          <div className="md:col-span-3">
            <div className="kicker mb-5">Explore</div>
            <ul className="space-y-3 text-sm text-white/75">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={`footer-nav-${l.label.toLowerCase()}`}
                    className="hover:text-[#C29B62] transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <div className="kicker mb-5">Get in touch</div>
            <ul className="space-y-3 text-sm text-white/75">
              <li>stephen.adesuyi@griddoffhomes.com</li>
              <li>888-874-6850</li>
              <li>By appointment · United States</li>
              <li>
                <a
                  href="https://griddoffhomesllc.com"
                  className="hover:text-[#C29B62] transition-colors"
                >
                  griddoffhomesllc.com
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row gap-6 md:items-center md:justify-between text-xs text-white/55">
          <div data-testid="footer-copy">
            © {year} Griddoff Homes LLC. All rights reserved. Equal Housing Opportunity.
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            <LegalDialog
              slug="privacy"
              trigger={
                <button
                  data-testid="footer-link-privacy"
                  className="hover:text-[#C29B62] transition-colors"
                >
                  Privacy
                </button>
              }
            />
            <LegalDialog
              slug="disclosure"
              trigger={
                <button
                  data-testid="footer-link-disclosure"
                  className="hover:text-[#C29B62] transition-colors"
                >
                  Disclosure
                </button>
              }
            />
            <LegalDialog
              slug="contact"
              trigger={
                <button
                  data-testid="footer-link-contact"
                  className="hover:text-[#C29B62] transition-colors"
                >
                  Contact
                </button>
              }
            />
            <LegalDialog
              slug="terms"
              trigger={
                <button
                  data-testid="footer-link-terms"
                  className="hover:text-[#C29B62] transition-colors"
                >
                  Terms &amp; Conditions
                </button>
              }
            />
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <div data-testid="app-root" className="bg-[#FAF9F6] text-[#2D312E]">
      <Header />
      <main>
        <Hero />
        <About />
        <Services />
        <Community />
        <Gallery />
        <Contact />
      </main>
      <Footer />
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}
